<script setup lang="ts">
import { HONEYPOT_FIELD_NAME, TURNSTILE_TOKEN_FIELD_NAME } from '#shared/hubspot'
import { faPaperPlane } from '@fortawesome/pro-light-svg-icons'
import { joinURL } from 'ufo'

const TURNSTILE_GRACE_MS = 8000

const props = defineProps<{
  id: UUID
}>()

const { data: formData, suspense } = useQuery({
  queryKey: ['hubspot', 'form', () => props.id] as const,
  queryFn({ queryKey, signal }) {
    const [, , formId] = queryKey
    return $fetch<unknown>(`/api/hubspot/form/${formId}`, { signal })
  },
})

await suspense()

const toast = getToast()

const site = useSiteConfig()

const alreadySubmitted = ref<boolean>(false)

const route = useRoute()

const title = useTitle()

const turnstile = ref<{ reset: () => void }>()

const turnstileHost = ref<HTMLElement>()

const turnstileBlocked = ref<boolean>(false)

const canReopenConsent = ref<boolean>(false)

const showConsentNotice = computed(() => turnstileBlocked.value && canReopenConsent.value)

let graceTimer: ReturnType<typeof setTimeout> | undefined

let hostObserver: MutationObserver | undefined

// Check for presence, not value: healthy resets clear it temporarily, while consent blocking prevents the field from being created.
function hasTurnstileInput() {
  return !!turnstileHost.value?.querySelector(`input[name="${TURNSTILE_TOKEN_FIELD_NAME}"]`)
}

function evaluateTurnstileAfterGrace() {
  clearTimeout(graceTimer)
  graceTimer = setTimeout(() => {
    // Check here instead of on mount because CookieYes can load after hydration.
    canReopenConsent.value = typeof window.revisitCkyConsent === 'function'
    turnstileBlocked.value = !hasTurnstileInput()
  }, TURNSTILE_GRACE_MS)
}

function reopenConsent() {
  window.revisitCkyConsent?.()
}

onMounted(() => {
  // A slow connection may load the widget after the deadline. Avoid showing a consent warning once the form works.
  hostObserver = new MutationObserver(() => {
    if (turnstileBlocked.value && hasTurnstileInput()) {
      turnstileBlocked.value = false
    }
  })

  if (turnstileHost.value) {
    hostObserver.observe(turnstileHost.value, { childList: true, subtree: true })
  }

  evaluateTurnstileAfterGrace()
  document.addEventListener('cookieyes_consent_update', evaluateTurnstileAfterGrace)
})

onBeforeUnmount(() => {
  clearTimeout(graceTimer)
  hostObserver?.disconnect()
  document.removeEventListener('cookieyes_consent_update', evaluateTurnstileAfterGrace)
})

const { mutate: submitForm, isPending } = useMutation({
  mutationKey: ['hubspot', 'form', () => props.id],

  mutationFn(event: Event) {
    if (alreadySubmitted.value) {
      throw new Error('multiple_submissions_attempt')
    }

    const body = new FormData(event.target as HTMLFormElement)

    body.set('hs_context', JSON.stringify({
      pageUri: joinURL(site.url, route.path),
      pageName: title.value,
    }))

    return $fetch<unknown>(`/api/hubspot/form/${props.id}`, {
      method: 'POST',
      body,
    })
  },

  onSuccess(data) {
    alreadySubmitted.value = true

    if (data.inlineMessage) {
      toast?.success({
        message: h('div', { innerHTML: data.inlineMessage }),
      })
    }
    else if (data.redirectUri) {
      const path = data.redirectUri.replace(site.url, '')
      navigateTo(path, {
        external: path === data.redirectUri,
      })
    }
  },

  onError() {
    // Turnstile tokens only work once. After a failed submit, retry with a new token
    turnstile.value?.reset()

    // Retrying cannot succeed while the widget is blocked, and the notice already says what to do instead
    if (showConsentNotice.value) {
      return
    }

    toast?.error('Innsendingen ble ikke godkjent. Prøv på nytt.')
  },
})
</script>

<template>
  <form @submit.prevent="submitForm">
    <slot name="header" />

    <div v-for="(fieldGroup, i) in formData?.fieldGroups" :key="i" class="flex gap-m flex-wrap">
      <HubspotFormField v-for="field in fieldGroup.fields" :key="field.name" class="grow" :field="field" />
    </div>

    <HubspotFormLegalConsentOptions
      v-if="formData?.legalConsentOptions"
      :data="formData.legalConsentOptions"
      class="text-carbon-400 flex flex-col gap-s"
    />

    <div aria-hidden="true" class="absolute -left-[9999px] size-px overflow-hidden">
      <input :name="HONEYPOT_FIELD_NAME" type="text" tabindex="-1" autocomplete="off">
    </div>

    <div ref="turnstileHost">
      <NuxtTurnstile ref="turnstile" :options="{ language: 'nb', theme: 'light' }" />
    </div>

    <div class="text-carbon-400 flex flex-col items-start gap-s">
      <div role="status" aria-live="polite">
        <p v-if="showConsentNotice">
          Sikkerhetssjekken som beskytter skjemaet mot søppelpost ser ut til å være blokkert, trolig av innstillingene dine for informasjonskapsler. Godta informasjonskapsler og last siden på nytt for å sende inn skjemaet.
        </p>
      </div>

      <button
        v-if="showConsentNotice"
        type="button"
        class="underline rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blurple-500"
        @click="reopenConsent"
      >
        Endre innstillinger for informasjonskapsler
      </button>
    </div>

    <Button
      :disabled="isPending || !formData || alreadySubmitted"
      variant="primary"
      color="blurple"
      type="submit"
      class="self-center light-mode"
      :title="alreadySubmitted ? 'Du har allerede sendt inn dette skjemaet.' : undefined"
    >
      {{ formData?.displayOptions?.submitButtonText || 'Send inn' }}

      <Spinner v-if="isPending" class="!size-5" color="carbon" />
      <Icon v-else :icon="faPaperPlane" />
    </Button>
  </form>
</template>
